import { Button, Card, CardContent, EmptyState, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Gift } from "lucide-react";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useGiftAction, useGifts } from "../../features/ops/queries.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/gifts")({ component: GiftsScreen });

function GiftsScreen() {
  const navigate = useNavigate();
  const principal = getStaffPrincipal();
  const canFulfil = hasPermission(principal, "gift:fulfil");
  const { data: gifts = [] } = useGifts(false);
  const action = useGiftAction();
  return (
    <div className="space-y-6">
      <PageHeader
        title="Gifts / ของขวัญ"
        subtitle="Only recipient-accepted gifts appear in the fulfilment queue."
      />
      {gifts.length === 0 ? (
        <EmptyState
          icon={<Gift />}
          title="No accepted gifts awaiting fulfilment"
          description="Pending/unaccepted gifts are never fulfillable by staff."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {gifts.map((gift) => (
            <Card key={gift.id}>
              <CardContent className="space-y-3 p-5">
                <button
                  type="button"
                  className="block text-left"
                  onClick={() => navigate({ to: "/gifts/$giftId", params: { giftId: gift.id } })}
                >
                  <p className="font-display font-semibold text-lg">
                    {gift.senderTableLabel} → {gift.recipientTableLabel}
                  </p>
                  <p className="text-muted-foreground text-sm">Accepted at {gift.acceptedAt}</p>
                </button>
                <div className="flex flex-wrap gap-2">
                  <StatusChip tone="info">accepted</StatusChip>
                  {gift.alcohol ? (
                    <StatusChip tone="warning">alcohol</StatusChip>
                  ) : (
                    <StatusChip tone="neutral">non-alcohol</StatusChip>
                  )}
                </div>
                <ul className="text-sm">
                  {gift.items.map((line) => (
                    <li key={line.id}>
                      {line.qty}× {line.name} · {formatMoney(line.qty * line.priceMinor)}
                    </li>
                  ))}
                </ul>
                {gift.message ? (
                  <p className="rounded-md bg-muted p-3 text-sm">“{gift.message}”</p>
                ) : null}
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button
                    disabled={!canFulfil}
                    title={canFulfil ? undefined : "Requires gift:fulfil"}
                    onClick={() => action.mutate({ giftId: gift.id, action: "fulfil" })}
                  >
                    Mark fulfilled
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: "/gifts/$giftId", params: { giftId: gift.id } })}
                  >
                    Staff reject / ปฏิเสธโดยสตาฟ
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
